import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '../api/client.js';
import { useAuth } from '../api/auth.js';
import { BellDevicePanel } from './BellDevicePanel.js';
import {
  Badge,
  Card,
  ConfirmDialog,
  Empty,
  Failure,
  Loading,
  Notice,
  Stat,
} from '../components/ui.js';
import {
  ACK_STAGE_LABELS,
  DEPLOY_STAGE,
  DEVICE_EVENT_LABELS,
  FAULT_LABELS,
  READINESS,
  formatBytes,
  formatDateTime,
  formatRelative,
} from '../lib/format.js';

interface DeviceDetail {
  id: string;
  name: string;
  serial: string;
  status: string;
  board_profile: string;
  firmware_version: string | null;
  school: { id: string; name: string; timezone: string };
  last_seen_at: string | null;
  last_state_at: string | null;
  boot_id: string | null;
  desired_revision: number;
  applied_revision: number;
  readiness: string;
  online: boolean;
  reasons: string[];
  state: {
    state?: string;
    clock_quality?: string;
    clock_offset_ms?: number | null;
    storage?: { state: string; total_bytes: number; free_bytes: number; asset_count: number };
    manifest_valid_until?: string | null;
    faults?: string[];
    audio?: { playing: boolean; priority: string };
    external_alarm_input?: boolean;
    uptime_ms?: number;
  } | null;
  deployments: Array<{
    revision: number;
    stage: string;
    event_count: number;
    asset_count: number;
    error: string | null;
    error_code: string | null;
    attempts: number;
    notified_at: string | null;
    applied_at: string | null;
  }>;
  active_incidents: Array<{ incident_id: string; kind: string; state: string; confirmed_at: string | null }>;
  recent_events: Array<{
    type: string;
    occurred_at: string;
    received_at: string;
    clock_reliable: boolean;
    event_id: string | null;
    details: Record<string, unknown>;
  }>;
}

const CLOCK_LABELS: Record<string, { label: string; tone: string; icon: string }> = {
  synced: { label: 'مزامَن', tone: 'ok', icon: '✔' },
  rtc_only: { label: 'من ساعة الجهاز فقط', tone: 'warn', icon: '▲' },
  unknown: { label: 'غير معروف', tone: 'danger', icon: '✖' },
  lost: { label: 'مفقود', tone: 'danger', icon: '✖' },
};

const STORAGE_LABELS: Record<string, { label: string; tone: string; icon: string }> = {
  ok: { label: 'سليم', tone: 'ok', icon: '✔' },
  low: { label: 'مساحة منخفضة', tone: 'warn', icon: '▲' },
  missing: { label: 'غير موجود', tone: 'danger', icon: '✖' },
  error: { label: 'خطأ', tone: 'danger', icon: '✖' },
};

export function DeviceDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [revokeReason, setRevokeReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const device = useQuery({
    queryKey: ['device', id],
    queryFn: () => get<DeviceDetail>(`/devices/${id}`),
    // Bell devices report every couple of seconds; their event log is worth following closely.
    refetchInterval: (q) => (q.state.data?.board_profile === 'esp32-dfplayer-bell' ? 5000 : 15_000),
    enabled: Boolean(id),
  });

  const commands = useQuery({
    queryKey: ['device-commands', id],
    queryFn: () =>
      get<{ items: Array<{ command_id: string; type: string; issued_at: string; ack_stage: string | null; ack_code: string | null; ack_at: string | null; reason: string | null }> }>(
        `/devices/${id}/commands?page_size=15`,
      ),
    enabled: Boolean(id),
    refetchInterval: 15_000,
  });

  const sync = useMutation({
    mutationFn: () => post(`/devices/${id}/commands`, { type: 'sync_manifest' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['device-commands', id] }),
    onError: (e: Error) => setError(e.message),
  });

  const status = useMutation({
    mutationFn: () => post(`/devices/${id}/commands`, { type: 'request_status' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['device-commands', id] }),
    onError: (e: Error) => setError(e.message),
  });

  const revoke = useMutation({
    mutationFn: () => post(`/devices/${id}/revoke`, { reason: revokeReason }),
    onSuccess: () => {
      setConfirmRevoke(false);
      void queryClient.invalidateQueries({ queryKey: ['device', id] });
    },
    onError: (e: Error) => setError(e.message),
  });

  if (device.isLoading) return <Loading />;
  if (device.error) return <Failure error={device.error} onRetry={() => void device.refetch()} />;
  const d = device.data!;

  const revokeDialog = confirmRevoke ? (
    <ConfirmDialog
      title="إلغاء الجهاز"
      danger
      busy={revoke.isPending}
      confirmLabel="إلغاء الجهاز نهائيًا"
      message={
        <>
          سيتم إبطال بيانات اعتماد الجهاز فورًا، ولن يستطيع تنزيل الجداول أو استقبال الأوامر.
          الجدول المخزّن محليًا قد يستمر بالعمل حتى انتهاء أفقه ما لم يُفصل الجهاز فعليًا.
        </>
      }
      onCancel={() => setConfirmRevoke(false)}
      onConfirm={() => revoke.mutate()}
    >
      <div className="field" style={{ marginTop: 12 }}>
        <label>سبب الإلغاء (يُسجَّل في التدقيق)</label>
        <input value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)} required />
      </div>
    </ConfirmDialog>
  ) : null;

  if (d.board_profile === 'esp32-dfplayer-bell') {
    return (
      <>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        <div className="row between" style={{ marginBottom: 14 }}>
          <div>
            <h2 style={{ margin: 0 }}>{d.name}</h2>
            <div style={{ color: 'var(--muted)', fontSize: 13.5 }}>
              <Link to={`/devices?school_id=${d.school.id}`}>{d.school.name}</Link> ·{' '}
              <span className="mono">{d.serial}</span> · جرس رقمي ESP32 + MP3
            </div>
          </div>
          <div className="row">
            {can('device.revoke') && d.status !== 'REVOKED' ? (
              <button className="danger" onClick={() => setConfirmRevoke(true)}>
                إلغاء الجهاز
              </button>
            ) : null}
          </div>
        </div>
        <BellDevicePanel device={d} />
        {revokeDialog}
      </>
    );
  }

  const r = READINESS[d.readiness] ?? READINESS.unknown!;
  const clock = CLOCK_LABELS[d.state?.clock_quality ?? 'unknown'] ?? CLOCK_LABELS.unknown!;
  const storage = STORAGE_LABELS[d.state?.storage?.state ?? 'ok'] ?? STORAGE_LABELS.ok!;

  return (
    <>
      {error ? <Notice tone="danger">{error}</Notice> : null}

      <div className="row between" style={{ marginBottom: 14 }}>
        <div>
          <h2 style={{ margin: 0 }}>{d.name}</h2>
          <div style={{ color: 'var(--muted)', fontSize: 13.5 }}>
            <Link to={`/devices?school_id=${d.school.id}`}>{d.school.name}</Link> ·{' '}
            <span className="mono">{d.serial}</span> · <span className="mono">{d.board_profile}</span>
          </div>
        </div>
        <div className="row">
          {can('device.manage') ? (
            <>
              <button onClick={() => status.mutate()} disabled={status.isPending}>
                طلب حالة
              </button>
              <button
                onClick={() => sync.mutate()}
                disabled={sync.isPending || d.desired_revision === 0}
              >
                إعادة مزامنة الجدول
              </button>
            </>
          ) : null}
          {can('device.revoke') && d.status !== 'REVOKED' ? (
            <button className="danger" onClick={() => setConfirmRevoke(true)}>
              إلغاء الجهاز
            </button>
          ) : null}
        </div>
      </div>

      {d.active_incidents.length > 0 ? (
        <Notice tone="danger">
          هذا الجهاز ضمن <span className="num">{d.active_incidents.length}</span> حادثة طوارئ نشطة.{' '}
          <Link to="/emergency">شاشة الطوارئ</Link>
        </Notice>
      ) : null}

      <div className="grid cols-4" style={{ marginBottom: 16 }}>
        <Stat
          label="الجاهزية"
          value={
            <Badge tone={r.tone} icon={r.icon}>
              {r.label}
            </Badge>
          }
        />
        <Stat
          label="آخر اتصال"
          value={
            d.online ? (
              <Badge tone="ok" icon="✔">
                متصل
              </Badge>
            ) : (
              <Badge tone="warn" icon="▲">
                منقطع
              </Badge>
            )
          }
          hint={formatRelative(d.last_seen_at)}
        />
        <Stat
          label="نسخة الجدول"
          value={
            <span className="num">
              {d.applied_revision}
              {d.applied_revision < d.desired_revision ? ` / ${d.desired_revision}` : ''}
            </span>
          }
          tone={d.applied_revision < d.desired_revision ? 'warn' : 'ok'}
          hint={d.applied_revision < d.desired_revision ? 'لم يطبّق أحدث نسخة' : 'مطابق للمنشور'}
        />
        <Stat
          label="الساعة"
          value={
            <Badge tone={clock.tone} icon={clock.icon}>
              {clock.label}
            </Badge>
          }
          hint={
            d.state?.clock_offset_ms !== null && d.state?.clock_offset_ms !== undefined
              ? `الانحراف ${d.state.clock_offset_ms} م.ث`
              : undefined
          }
        />
      </div>

      {d.reasons.length > 0 ? (
        <Notice tone={d.readiness === 'not_ready' ? 'danger' : 'warn'}>
          <ul style={{ margin: 0, paddingInlineStart: 18 }}>
            {d.reasons.map((reason, i) => (
              <li key={i}>{reason}</li>
            ))}
          </ul>
        </Notice>
      ) : null}

      <div className="grid cols-2">
        <Card title="الحالة المبلّغة من الجهاز">
          {!d.state ? (
            <Empty title="لم تصل حالة بعد" hint="ستظهر بعد أول اتصال من الجهاز." />
          ) : (
            <table>
              <tbody>
                <tr>
                  <th>الوضع</th>
                  <td>{d.state.state ?? '—'}</td>
                </tr>
                <tr>
                  <th>التخزين</th>
                  <td>
                    <Badge tone={storage.tone} icon={storage.icon}>
                      {storage.label}
                    </Badge>{' '}
                    {d.state.storage
                      ? `${formatBytes(d.state.storage.free_bytes)} متاح من ${formatBytes(d.state.storage.total_bytes)}`
                      : ''}
                  </td>
                </tr>
                <tr>
                  <th>الملفات المخزنة</th>
                  <td className="num">{d.state.storage?.asset_count ?? 0}</td>
                </tr>
                <tr>
                  <th>صلاحية الجدول المحلي</th>
                  <td>{formatDateTime(d.state.manifest_valid_until, d.school.timezone)}</td>
                </tr>
                <tr>
                  <th>الإخراج الصوتي الآن</th>
                  <td>
                    {d.state.audio?.playing ? (
                      <Badge tone="info" icon="▶">
                        بدأ الإخراج ({d.state.audio.priority})
                      </Badge>
                    ) : (
                      <Badge tone="muted" icon="■">
                        متوقف
                      </Badge>
                    )}
                  </td>
                </tr>
                <tr>
                  <th>مدخل الإنذار الخارجي</th>
                  <td>
                    {d.state.external_alarm_input ? (
                      <Badge tone="danger" icon="⚠">
                        مفعّل
                      </Badge>
                    ) : (
                      <Badge tone="muted" icon="—">
                        غير مفعّل
                      </Badge>
                    )}
                  </td>
                </tr>
                <tr>
                  <th>الأعطال</th>
                  <td>
                    {(d.state.faults ?? []).length === 0 ? (
                      <Badge tone="ok" icon="✔">
                        لا يوجد
                      </Badge>
                    ) : (
                      <div className="pill-list">
                        {d.state.faults!.map((f) => (
                          <Badge key={f} tone="danger" icon="✖">
                            {FAULT_LABELS[f] ?? f}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
                <tr>
                  <th>آخر تقرير حالة</th>
                  <td>{formatDateTime(d.last_state_at, d.school.timezone)}</td>
                </tr>
              </tbody>
            </table>
          )}
          <Notice tone="info">
            «بدأ الإخراج الصوتي» يعني أن الجهاز بدأ التشغيل على المخرج. لا يوجد حساس لقياس الصوت،
            فلا يمكن للنظام تأكيد أن الصوت سُمع فعلًا في المدرسة.
          </Notice>
        </Card>

        <Card title="نسخ الجدول على هذا الجهاز">
          {d.deployments.length === 0 ? (
            <Empty title="لم يُنشر جدول لهذا الجهاز بعد" />
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>النسخة</th>
                    <th>الحالة</th>
                    <th>الأحداث</th>
                    <th>الملفات</th>
                    <th>التطبيق</th>
                  </tr>
                </thead>
                <tbody>
                  {d.deployments.map((dep) => {
                    const stage = DEPLOY_STAGE[dep.stage] ?? DEPLOY_STAGE.PENDING!;
                    return (
                      <tr key={dep.revision}>
                        <td className="num">{dep.revision}</td>
                        <td>
                          <Badge tone={stage.tone} icon={stage.icon}>
                            {stage.label}
                          </Badge>
                          {dep.error ? (
                            <div style={{ fontSize: 12, color: 'var(--danger)' }}>{dep.error}</div>
                          ) : null}
                        </td>
                        <td className="num">{dep.event_count}</td>
                        <td className="num">{dep.asset_count}</td>
                        <td style={{ fontSize: 12.5 }}>
                          {formatDateTime(dep.applied_at, d.school.timezone)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <Card title="آخر الأوامر" subtitle="«تم الإرسال» لا يساوي «تم التشغيل»">
        {commands.isLoading ? (
          <Loading />
        ) : (commands.data?.items.length ?? 0) === 0 ? (
          <Empty title="لا توجد أوامر" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>النوع</th>
                  <th>الإصدار</th>
                  <th>حالة الاستلام</th>
                  <th>الرمز</th>
                  <th>السبب</th>
                </tr>
              </thead>
              <tbody>
                {commands.data!.items.map((c) => {
                  const ack = c.ack_stage ? ACK_STAGE_LABELS[c.ack_stage] : null;
                  return (
                    <tr key={c.command_id}>
                      <td className="mono">{c.type}</td>
                      <td style={{ fontSize: 12.5 }}>
                        {formatDateTime(c.issued_at, d.school.timezone)}
                      </td>
                      <td>
                        {ack ? (
                          <Badge tone={ack.tone}>{ack.label}</Badge>
                        ) : (
                          <Badge tone="muted" icon="…">
                            لم يصل ACK
                          </Badge>
                        )}
                      </td>
                      <td className="mono" style={{ fontSize: 12.5 }}>
                        {c.ack_code ?? '—'}
                      </td>
                      <td style={{ fontSize: 12.5, color: 'var(--muted)' }}>{c.reason ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="آخر أحداث الجهاز">
        {d.recent_events.length === 0 ? (
          <Empty title="لا توجد أحداث مسجلة" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>الحدث</th>
                  <th>وقت الجهاز</th>
                  <th>وقت الاستلام</th>
                  <th>الساعة موثوقة</th>
                </tr>
              </thead>
              <tbody>
                {d.recent_events.map((e, i) => (
                  <tr key={i}>
                    <td>{DEVICE_EVENT_LABELS[e.type] ?? e.type}</td>
                    <td style={{ fontSize: 12.5 }}>
                      {formatDateTime(e.occurred_at, d.school.timezone)}
                    </td>
                    <td style={{ fontSize: 12.5 }}>
                      {formatDateTime(e.received_at, d.school.timezone)}
                    </td>
                    <td>
                      {e.clock_reliable ? (
                        <Badge tone="ok" icon="✔">
                          نعم
                        </Badge>
                      ) : (
                        <Badge tone="warn" icon="▲">
                          لا
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {revokeDialog}
    </>
  );
}
